/**
 * OrderTableItem (OrderTableItem.jsx)
 * 
 * \src\components\IncomingOrders\IncomingOrdersTable\OrderTableItem.jsx
 */

import { memo, useMemo } from "react";
import { FormattedDate, useIntl } from "react-intl";
import { Badge, Chip, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import ColumnType from "../helpers/ColumnType";
import mapOrder from "../helpers/orderMapper";

// Hjälpfunktion för typ-ikon + label
const getTypeInfo = (order) => {
  const type = order.orderType || order.type;
  switch (type) {
    case "TEXTIL": return { icon: "👕", label: "Textil" };
    case "LAGER": return { icon: "🏷", label: "Lager" };
    case "INDUSTRI": return { icon: "🏭", label: "Industri" };
    case "TRYCK": return { icon: "🖨", label: "Tryck" };
    default: return { icon: "📦", label: type || "Okänd" };
  }
};

const getStatusColor = (status) => {
  if (!status) return "default";
  const s = status.toLowerCase();
  if (s.includes("klar") || s.includes("levererad")) return "success";
  if (s.includes("pågår") || s.includes("behandlas")) return "info";
  if (s.includes("avbruten") || s.includes("makulerad")) return "error";
  return "default";
};

const safeFormattedDate = (date) => date && <FormattedDate value={date} />;

/**
 * visibleFields-objektet styr vilka kolumner som visas.
 * Exempel:
 *  {
 *    orderType: true,
 *    ourOrderNo: true,
 *    ...
 *    rowCount: true,
 *  }
 */
const OrderTableItem = ({ order: unmappedOrder, onShowOrderDetails, visibleFields = {} }) => {
  const order = useMemo(
    () => mapOrder(unmappedOrder),
    [unmappedOrder]
  );

  const show = (field) => visibleFields[field] !== false;
  const intl = useIntl();

  // Inline komponent för status-Chip
  const StatusChip = ({ status }) => (
    <Chip
      label={status || "Okänd"}
      size="small"
      color={getStatusColor(status)}
      variant={getStatusColor(status) === "default" ? "outlined" : "filled"}
    />
  );

  function renderOrderRowCount() {
    const { orderRowCount } = order.derivedFields;

    return (
      <Chip
        label={`${orderRowCount} rader`}
        size="small"
        variant="outlined"
      />
    );
  };

  function renderOrderRowProductSummary() {
    const { orderRowProductSummary } = order.derivedFields;

    const products = orderRowProductSummary.map(n => n.product);

    return (
      <Tooltip
        title={
          <Typography sx={{ whiteSpace: "pre-line" }}>
            {products.join("\n")}
          </Typography>
        }
      >
        <Chip
          label={products.join(", ")}
          size="small"
          sx={{ maxWidth: "200px" }}
        />
      </Tooltip>
    );
  };

  function renderOrderRowSourceSummary() {
    //const { orderRowSourceSummary } = order.derivedFields;
    //const sources = orderRowSourceSummary.map(n => n.source);

    return "Webbshopp"; // TODO
  };

  function renderOrderRowTypeSummary() {
    const { orderRowTypeSummary } = order.derivedFields;

    const count = orderRowTypeSummary.reduce((acc, cur) => acc + cur.count, 0); // TODO
    const icon = "📦";
    const label = "Okänd";

    return (
      <Tooltip
        title={
          <Typography sx={{ whiteSpace: "pre-line" }}>
            {count}&ensp;{icon}&nbsp;{label}
          </Typography>
        }
      >
        <Badge
          badgeContent={count}
          overlap="circular"
          slotProps={{
            badge: {
              sx: {
                background: "#ffff008f",
                color: "#888",
                m: -0.25,
              },
            },
          }}
        >
          {icon}
        </Badge>
      </Tooltip>
    );
  };

  const handleRowClick = (e) => {
    e.preventDefault();
    const { orderId, supplierOrderId } = order;
    onShowOrderDetails?.({ orderId, supplierOrderId });
  };

  return (
    <TableRow hover sx={{ cursor: "pointer" }} onClick={handleRowClick}>
      {show(ColumnType.ORDER_TYPE) && (
        <TableCell>
          {
            renderOrderRowTypeSummary()
          }
        </TableCell>
      )}
      {true && show(ColumnType.SUPPLIER_ORDER_NO) && <TableCell>{order.supplierOrderNo}</TableCell>}
      {true && show(ColumnType.SUPPLIER_ORDER_REFERENCE) && <TableCell>{order.supplierOrderReference}</TableCell>}
      {false && show(ColumnType.ORDER_ID) && <TableCell>{order.orderId}</TableCell>}
      {true && show(ColumnType.ORDER_REFERENCE) && <TableCell>{order.orderReference}</TableCell>}
      {show(ColumnType.RECEIVER) && <TableCell>{order.orderingCompany}</TableCell>}
      {show(ColumnType.ORDER_DATE) && <TableCell>{safeFormattedDate(order.orderDate)}</TableCell>}
      {show(ColumnType.SUMMARY) && (
        <TableCell>
          {
            renderOrderRowProductSummary()
          }
        </TableCell>
      )}
      {show(ColumnType.DELIVERY_ADDRESS_2) && <TableCell>{order.deliveryAddress.address2}</TableCell>}
      {show(ColumnType.DELIVERY_DATE) && <TableCell>{safeFormattedDate(order.deliveryDateWish)}</TableCell>}
      {show(ColumnType.ORDER_STATUS) && (<TableCell><StatusChip status={intl.formatMessage({ id: "incomingOrders.orderstatus." + order.orderStatus })}/></TableCell>)}
      {show(ColumnType.SOURCE) && <TableCell>
        {
          renderOrderRowSourceSummary()
        }
      </TableCell>}
      {show(ColumnType.ROW_COUNT) && (
        <TableCell align="right">
          {
            renderOrderRowCount()
          }
        </TableCell>
      )}
    </TableRow>
  );
};

OrderTableItem.displayName = "OrderTableItem";

export default memo(OrderTableItem);
